import { SCREENS } from '../../../services/exports/Constants';
import React, { FC, PropsWithChildren } from 'react';
import FixedPageLayout from '../../../components-library/layouts/FixedPageLayout';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import useRouter from '../../../hooks/navigation/useRouter';
import { useGiftCardsCheckoutErrors } from '../../../hooks/payment/useGiftCardCheckout';
import PayButton from '../../../features/gift-cards/components/PayButton';
import TrustLabel from '../../../components-library/labels/TrustLabel';
import CustomerInfoSection from '../../../components-library/checkout/sections/CustomerInfoSection';
import PaymentInfoSection from '../../../components-library/checkout/sections/PaymentInfoSection';
import InvoiceInfoSection from '../../../components-library/checkout/sections/InvoiceInfoSection';
import Section from '../../../components-library/checkout/sections/Section';
import GiftCardsCheckoutScreen from './GiftCardsCheckoutScreen';
import useNavigate from '../../../hooks/navigation/useNavigate';
import useMobileApp from '../../../hooks/mobile-app/useMobileApp';
import useAuth from '../../../hooks/global/useAuth';
import Button from '../../../modules/mobile/components/buttons/Button';
import { Modals } from '../../../modules/mobile/exports/Enums';
import useModalRouter from '../../../modules/mobile/common/hooks/useModalRouter';
import TermsLink from '../../../components-library/legal/TermsLink';
import BackIcon from '../../../assets/logo/BackIcon';
import useTheme from '../../../hooks/ui/useTheme';
import { useGiftCardsBasket } from '../../../hooks/basket/useGiftCardBasket';
import useAppMethods from '../../../hooks/utility/useAppMethods';
import useGiftCardExcludedPaymentMethods from '../../../features/gift-cards/hooks/useGiftCardExcludedPaymentMethods';

const MobileGiftCardsCheckoutScreen: FC = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Pages:Checkout:OrderConfirmationScreen' });

  const { validationErrors } = useGiftCardsCheckoutErrors();
  const { navigate } = useRouter();
  const theme = useTheme();
  const { isMobileApp } = useMobileApp();
  const excludedPaymentMethods = useGiftCardExcludedPaymentMethods();

  const _onReturn = () => navigate({ web: { to: SCREENS.HOME_SCREEN } });

  return (
    <>
      <FixedPageLayout hasHeader={false} footer={<Footer />}>
        <>
          {!isMobileApp && (
            <button
              onClick={_onReturn}
              className="flex items-center w-full px-md pt-md text-sm text-muted-foreground lg:p-none lg:w-fit"
            >
              <BackIcon className="h-3 !w-3 mr-xs" color={theme.color.gray['700']} />
              {t('buttons.return')}
            </button>
          )}
          <div className={classnames('grid grid-cols-12')}>
            <div className="col-span-12 lg:col-span-8 xl:col-span-9 lg:py-lg lg:px-2xl space-y-sm py-md">
              <TrustLabel className="m-md" />
              <CustomerInfoSection show validationErrors={validationErrors} />
              <InvoiceInfoSection show validationErrors={validationErrors} />
              <PaymentInfoSection show excludedPaymentMethods={excludedPaymentMethods} />
            </div>
            <div className={'col-span-12 lg:col-span-4 xl:col-span-3 lg:p-lg lg:h-screen lg:sticky lg:top-0'}>
              <Section
                show
                title={t('sections.order_summary')}
                className="!p-md space-y-md bg-white !rounded-lg border"
                titleClassName="!text-brand-text-primary !text-md font-semibold"
                containerClassName="p-md"
              >
                <GiftCardsCheckoutScreen.GiftCardRow />
                <GiftCardsCheckoutScreen.DeliveryRow />
                <GiftCardsCheckoutScreen.TotalRow />
              </Section>
            </div>
          </div>
        </>
      </FixedPageLayout>
    </>
  );
};

function Footer() {
  const { isMobileApp } = useMobileApp();

  return isMobileApp ? <MobileAppFooter /> : <DefaultFooter />;
}

function DefaultFooter() {
  const { t: tPayBtn } = useTranslation(undefined, { keyPrefix: 'Components:Payment:Buttons:PayButton' });

  const { generatePath } = useNavigate();
  const { total } = useGiftCardsBasket();
  const { formatCurrency } = useAppMethods();

  return (
    <>
      <ButtonWrapper>
        <PayButton title={tPayBtn('pay_now', { amount: formatCurrency(total) })} />
        <TermsLink termsUrl={generatePath(SCREENS.GIFT_CARDS_TERMS_AND_CONDITIONS)} />
      </ButtonWrapper>
    </>
  );
}

function MobileAppFooter() {
  const { isGuest } = useAuth();
  const { openModal } = useModalRouter();
  const { t } = useTranslation(null, { keyPrefix: 'Modules:Mobile:Pages:CartPage.buttons' });

  if (isGuest) {
    return (
      <ButtonWrapper>
        <Button className="h-10" title={t('login_to_checkout')} onClick={() => openModal({ id: Modals.Login })} />
      </ButtonWrapper>
    );
  }

  return <DefaultFooter />;
}

const ButtonWrapper: FC<PropsWithChildren> = ({ children }) => {
  const { isMobileApp } = useMobileApp();

  return (
    <div
      className={classnames('px-sm bg-brand-card border-t pt-md', {
        'safe-area-bottom': isMobileApp,
        'pb-sm': !isMobileApp,
      })}
    >
      {children}
    </div>
  );
};

export default MobileGiftCardsCheckoutScreen;
